import React, { useMemo, useState } from 'react';
import { Alert, Button, DatePicker, Divider, Form, Modal, Tooltip, message } from 'antd';
import { ClockCircleOutlined, ThunderboltOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getOrderActualTimeEstimate, operatorEditOrderActualTimes } from '../modules/orders/api';

const DT_FORMAT = 'YYYY-MM-DD HH:mm';

// Latest log (by createdAt) whose status matches, case-insensitive.
const latestLog = (logs, statuses) => {
  const up = statuses.map((s) => s.toUpperCase());
  const matches = (logs || []).filter((l) => up.includes(String(l.status || '').toUpperCase()));
  if (!matches.length) return null;
  return matches.reduce((a, b) => (new Date(a.createdAt) > new Date(b.createdAt) ? a : b));
};

// The four stop moments, in chronological order. Each maps to the status log
// the backend edits, and is offered only when that log exists — so a
// collected-but-not-delivered order shows the pickup pair alone.
const MOMENTS = [
  { name: 'pickupArrivalTime', label: 'Pickup arrival', leg: 'pickup', statuses: ['PICKUP_ARRIVED'] },
  { name: 'pickupFinishTime', label: 'Collected (pickup done)', leg: 'pickup', statuses: ['COLLECTED'] },
  {
    name: 'actualArrivalTime',
    label: 'Delivery arrival',
    leg: 'delivery',
    statuses: ['DELIVERY_ARRIVED', 'SERVICE_ARRIVED'],
  },
  { name: 'actualFinishTime', label: 'Delivered (finish)', leg: 'delivery', statuses: ['COMPLETED'] },
];

/**
 * Operator correction of an order's recorded stop times — BOTH legs. Drivers
 * sometimes log a stop late (e.g. validating the last delivery only on arrival
 * at the hub, or batch-marking pickups), which distorts the route detail and
 * inflates the billed client-service time. This edits the underlying status-log
 * timestamps; "Estimate from route" infers realistic values the same way the
 * shift-time editor does (planned stop time shifted by the drift at the
 * previous stop). Only the moments the order has actually reached are shown.
 * Admin/USER only — not rendered for viewers.
 */
export default function OrderActualTimeEditor({ order, onSuccess }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [estimating, setEstimating] = useState(false);
  // Estimate feedback shown INLINE in the modal — a toast would render at the
  // top of the viewport, behind the app's fixed nav bar (unreadable).
  const [estimateInfo, setEstimateInfo] = useState(null);
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  // A moment is editable only if its log exists — there's nothing to correct
  // otherwise, and the backend would just report it as skipped.
  const available = useMemo(
    () =>
      MOMENTS.map((m) => ({ ...m, current: latestLog(order?.logs, m.statuses)?.createdAt })).filter((m) => m.current),
    [order?.logs],
  );
  const hasPickup = available.some((m) => m.leg === 'pickup');
  const hasDelivery = available.some((m) => m.leg === 'delivery');

  const openModal = () => {
    setEstimateInfo(null);
    form.setFieldsValue(available.reduce((acc, m) => ({ ...acc, [m.name]: dayjs(m.current) }), {}));
    setOpen(true);
  };

  const onEstimate = async () => {
    setEstimating(true);
    try {
      const { response } = await getOrderActualTimeEstimate(order.orderCode);
      const legs = response?.legs || {};
      const next = {};
      available.forEach((m) => {
        const leg = legs[m.leg];
        const v = m.name.endsWith('ArrivalTime') ? leg?.actualArrivalTime : leg?.actualFinishTime;
        if (v) next[m.name] = dayjs(v);
      });
      if (!Object.keys(next).length) {
        setEstimateInfo({ type: 'warning', text: "Couldn't infer times from the route." });
        return;
      }
      form.setFieldsValue(next);
      // Each leg has its own drift (the driver can fall further behind between
      // pickup and delivery), so report every leg we actually filled.
      const drifts = [...new Set(available.map((m) => m.leg))]
        .filter((leg) => legs[leg]?.basis?.driftMins != null)
        .map((leg) => `${leg} ${legs[leg].basis.driftMins > 0 ? '+' : ''}${legs[leg].basis.driftMins} min`);
      setEstimateInfo({
        type: 'info',
        text: drifts.length
          ? `Inferred from the route — drift vs plan: ${drifts.join(', ')}. Review and save.`
          : 'Inferred from the route. Review and save.',
      });
    } catch (e) {
      setEstimateInfo({ type: 'error', text: e?.message || 'Failed to estimate' });
    } finally {
      setEstimating(false);
    }
  };

  const onSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    // Same chain the backend enforces — catch it here so the operator gets the
    // message without a round-trip.
    const chain = available.map((m) => ({ label: m.label, at: values[m.name] })).filter((c) => c.at);
    for (let i = 0; i < chain.length - 1; i += 1) {
      if (chain[i].at.isAfter(chain[i + 1].at)) {
        setEstimateInfo({ type: 'error', text: `${chain[i].label} must be before ${chain[i + 1].label}.` });
        return;
      }
    }
    const payload = {};
    available.forEach((m) => {
      const v = values[m.name];
      // Send only what the operator actually changed.
      if (v && !dayjs(m.current).isSame(v, 'minute')) payload[m.name] = v.toISOString();
    });
    if (!Object.keys(payload).length) {
      setEstimateInfo({ type: 'warning', text: 'Nothing changed.' });
      return;
    }
    setSaving(true);
    try {
      const { response } = await operatorEditOrderActualTimes(order.orderCode, payload);
      const skipped = response?.skipped || [];
      messageApi.success(skipped.length ? `Times updated (no log for: ${skipped.join(', ')}).` : 'Times updated.');
      setOpen(false);
      onSuccess && onSuccess();
    } catch (e) {
      setEstimateInfo({ type: 'error', text: e?.message || 'Failed to update' });
    } finally {
      setSaving(false);
    }
  };

  // Nothing recorded yet — nothing to fix.
  if (!available.length) return null;

  return (
    <>
      {contextHolder}
      <Tooltip title="Correct the recorded pickup / delivery times (e.g. the driver logged a stop late). Updates the audit log.">
        <Button icon={<ClockCircleOutlined />} onClick={openModal} style={{ marginTop: 8 }}>
          Fix times
        </Button>
      </Tooltip>

      <Modal
        open={open}
        title="Fix recorded times"
        onCancel={() => setOpen(false)}
        onOk={onSave}
        confirmLoading={saving}
        okText="Save"
        destroyOnClose
      >
        <p style={{ color: 'var(--text-muted)', marginTop: 0 }}>
          Correct when this order was actually picked up and delivered. This edits the recorded times and is logged in
          the audit trail. Use <strong>Estimate from route</strong> to infer realistic values from the optimizer plan
          and the driver&apos;s progress.
        </p>
        <Button icon={<ThunderboltOutlined />} onClick={onEstimate} loading={estimating} style={{ marginBottom: 16 }}>
          Estimate from route
        </Button>
        {estimateInfo && (
          <Alert type={estimateInfo.type} message={estimateInfo.text} showIcon style={{ marginBottom: 16 }} />
        )}
        <Form form={form} layout="vertical" requiredMark={false}>
          {hasPickup && hasDelivery && (
            <Divider orientation="left" plain style={{ marginTop: 0 }}>
              Pickup
            </Divider>
          )}
          {available
            .filter((m) => m.leg === 'pickup')
            .map((m) => (
              <Form.Item key={m.name} label={m.label} name={m.name}>
                <DatePicker showTime format={DT_FORMAT} style={{ width: '100%' }} minuteStep={1} />
              </Form.Item>
            ))}
          {hasPickup && hasDelivery && (
            <Divider orientation="left" plain>
              Delivery
            </Divider>
          )}
          {available
            .filter((m) => m.leg === 'delivery')
            .map((m) => (
              <Form.Item key={m.name} label={m.label} name={m.name}>
                <DatePicker showTime format={DT_FORMAT} style={{ width: '100%' }} minuteStep={1} />
              </Form.Item>
            ))}
        </Form>
      </Modal>
    </>
  );
}
